import { CARD_OVERLAY, CLIENT_THRESHOLDS, MESSAGES } from '../config.js';
import type { FrameVerdict } from './gate.js';
import {
  cropGray,
  detectCardInBox,
  exposureStats,
  glareStats,
  laplacianVariance,
  motionScore,
  type GrayFrame,
  type Rect,
} from './metrics.js';

/**
 * Per-frame card-stage assessment — §8 metrics + §9 hard-gate ordering. Returns the §10
 * guidance message for the first failing gate; ranking score only for passing frames.
 */

export interface CardAssessment {
  verdict: FrameVerdict;
  message: string;
  cardPresent: boolean;
}

export function innerCardRect(box: Rect): Rect {
  const r = CARD_OVERLAY.innerRegionRatio;
  return {
    x: box.x + (box.w * (1 - r)) / 2,
    y: box.y + (box.h * (1 - r)) / 2,
    w: box.w * r,
    h: box.h * r,
  };
}

export function assessCardFrame(
  frame: GrayFrame,
  prev: GrayFrame | null,
  overlayBox: Rect,
  timestampMs: number,
): CardAssessment {
  const t = CLIENT_THRESHOLDS;
  const card = detectCardInBox(frame, overlayBox, {
    borderBandRatio: CARD_OVERLAY.borderBandRatio,
    edgeDensityMin: t.cardEdgeDensityMin,
    sidesMin: t.cardEdgeSidesMin,
    innerContrastMin: t.cardInnerContrastMin,
  });
  const fail = (message: string, sharp = true): CardAssessment => ({
    verdict: { timestampMs, pass: false, sharp, rankScore: 0 },
    message,
    cardPresent: card.present,
  });

  const inner = cropGray(frame, innerCardRect(overlayBox));
  const exposure = exposureStats(inner);
  const glare = glareStats(cropGray(frame, overlayBox));
  const sharpness = laplacianVariance(inner);
  const motion = prev ? motionScore(cropGray(frame, overlayBox), cropGray(prev, overlayBox)) : 0;

  // §9 — hard gates in order; the first failure drives the §10 UI message.
  if (!card.present) {
    if (card.sidesDetected >= 2) return fail(MESSAGES.misaligned);
    return fail(MESSAGES.cardMissing);
  }
  if (motion > t.motionMax) return fail(MESSAGES.stillPlease, false);
  if (glare.ratio > t.glareRatioMax || glare.maxBlockRatio > t.glareBlockMax) {
    return fail(MESSAGES.adjustGlare);
  }
  if (exposure.underExposureRatio > t.underExposureMax || exposure.mean < 40) {
    return fail(MESSAGES.lowLight);
  }
  if (exposure.overExposureRatio > t.overExposureMax) return fail(MESSAGES.adjustGlare);
  if (exposure.contrast < t.contrastMin) return fail(MESSAGES.lowLight);
  if (sharpness < t.cardSharpnessMin) return fail(MESSAGES.blur, false);

  const exposureQuality = Math.max(0, 1 - exposure.underExposureRatio - exposure.overExposureRatio);
  // §9 ranking — normalized against fixed provisional scales for the analysis size.
  const rankScore =
    0.5 * Math.min(1, sharpness / 300) +
    0.2 * Math.min(1, exposure.contrast / 80) +
    0.15 * exposureQuality +
    0.15 * Math.max(0, 1 - motion / t.motionMax);

  return {
    verdict: { timestampMs, pass: true, sharp: true, rankScore },
    message: MESSAGES.ready,
    cardPresent: true,
  };
}

/** Face-stage per-frame photo checks (§18): sharpness + lighting on the face ROI. */
export interface FacePhotoAssessment {
  sharp: boolean;
  lightingOk: boolean;
  motionOk: boolean;
}

export function assessFacePhoto(
  frame: GrayFrame,
  prev: GrayFrame | null,
  faceBox: Rect,
): FacePhotoAssessment {
  const t = CLIENT_THRESHOLDS;
  const roi = cropGray(frame, faceBox);
  const exposure = exposureStats(roi);
  const sharpness = laplacianVariance(roi);
  const motion = prev ? motionScore(roi, cropGray(prev, faceBox)) : 0;
  return {
    sharp: sharpness >= t.faceSharpnessMin,
    lightingOk:
      exposure.underExposureRatio <= t.underExposureMax &&
      exposure.overExposureRatio <= t.overExposureMax &&
      exposure.mean >= 40 &&
      exposure.contrast >= t.contrastMin,
    motionOk: motion <= t.motionMax * 1.5,
  };
}
